import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import type { FinancialFormValues } from "../adapaters/financialFormSchema";
import type { MonetaryWithFrequency } from "../types/common";

export function FixedExtras() {
  const { control, setValue } = useFormContext<FinancialFormValues>();

  const fixedOther = useWatch({ control, name: "fixedOther" });

  type OtherCost = MonetaryWithFrequency<number, string> & { label: string };

  const otherList: OtherCost[] = (
    Array.isArray(fixedOther) ? fixedOther : []
  ) as OtherCost[];

  const addOther = () => {
    if ((otherList?.length ?? 0) >= 5) return;
    const next = [...otherList, { label: "", amount: 0, frequency: "M" }];
    setValue("fixedOther", next as any, { shouldDirty: true });
  };

  const removeOther = (idx: number) => {
    const next = otherList.filter((_, i) => i !== idx);
    setValue("fixedOther", next as any, { shouldDirty: true });
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Typography variant="subtitle1" fontWeight="bold">
          Savings (monthly)
        </Typography>
        <Stack direction="row" gap={2} alignItems="center">
          <Controller
            control={control}
            name="monthlySavingsAmount"
            rules={{ min: 0 }}
            render={({ field }) => (
              <NumericFormat
                id="savings-amount"
                customInput={TextField as any}
                value={field.value ?? 0}
                decimalSeparator="."
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator=","
                prefix="£"
                allowNegative={false}
                onValueChange={(vals) => field.onChange(vals.floatValue ?? 0)}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />
          <TextField select size="small" value="M" disabled sx={{ width: 140 }}>
            <MenuItem value="M">Monthly</MenuItem>
          </TextField>
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight="bold">
          Other Essential Costs (up to 5)
        </Typography>
        {otherList.map((entry, idx) => (
          <Stack
            key={`fixedOther.${idx}.label` as const}
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Controller
              control={control}
              name={`fixedOther.${idx}.label` as const}
              render={({ field }) => (
                <TextField
                  label="Label"
                  size="small"
                  sx={{ width: "30%" }}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name={`fixedOther.${idx}.amount` as const}
              rules={{ min: 0 }}
              render={({ field }) => (
                <NumericFormat
                  customInput={TextField}
                  value={field.value ?? 0}
                  thousandSeparator=","
                  decimalSeparator="."
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="£"
                  allowNegative={false}
                  onValueChange={(vals) => field.onChange(vals.floatValue ?? 0)}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            <Controller
              control={control}
              name={`fixedOther.${idx}.frequency` as const}
              render={({ field }) => (
                <TextField
                  select
                  label="Frequency"
                  size="small"
                  sx={{ width: "20%" }}
                  {...field}
                >
                  <MenuItem value="M">Monthly</MenuItem>
                  <MenuItem value="W">Weekly</MenuItem>
                  <MenuItem value="F">Fortnightly</MenuItem>
                  <MenuItem value="A">Annually</MenuItem>
                  <MenuItem value="4W">4-Weekly</MenuItem>
                  <MenuItem value="Q">Quarterly</MenuItem>
                </TextField>
              )}
            />

            <Button
              variant="text"
              color="error"
              onClick={() => removeOther(idx)}
            >
              Remove
            </Button>
          </Stack>
        ))}

        {otherList.length < 5 && (
          <Button
            variant="outlined"
            sx={{
              borderWidth: "2px",
              borderStyle: "dashed",
              borderColor: "primary.dark",
              background: "none",
              width: "100%",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.dark",
              fontWeight: 500,
              fontSize: "1rem",
              mb: 2,
              "&:hover": { background: "primary.light" },
            }}
            onClick={addOther}
          >
            + Add another
          </Button>
        )}
      </Box>
    </Box>
  );
}
